import { useCallback, useEffect, useState } from 'react';

/**
 * Measures the container's real pixel width so a chart can draw its viewBox 1:1 with CSS pixels.
 * A fixed viewBox scaled to `width="100%"` shrinks/enlarges axis text with the container (about
 * 20px labels on desktop, 5px on a phone); drawing at the real width keeps text a constant size.
 * Falls back to `fallback` until measured (first paint, jsdom where nothing has layout).
 * Returns a callback ref, so it also works when the container mounts after an early-return branch.
 */
export function useChartWidth(fallback = 520): [(el: HTMLDivElement | null) => void, number] {
    const [el, setEl] = useState<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(fallback);
    const ref = useCallback((node: HTMLDivElement | null) => setEl(node), []);

    useEffect(() => {
        if (!el) return undefined;
        const apply = (w: number) => {
            const rounded = Math.round(w);
            if (rounded >= 120) setWidth(prev => (prev === rounded ? prev : rounded));
        };
        apply(el.getBoundingClientRect().width);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(entries => {
            for (const e of entries) apply(e.contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [el]);

    return [ref, width];
}
